import React, { type Dispatch, type SetStateAction } from "react";

import type { EtsyIntegration } from "src/interfaces/plaidIntegration.interface";

import PlatformIntegrationsTableRow from "../PlatformIntegrationsTableRow";
import useIsRunning from "../useIsRunning";
import type { DialogState } from "../types";
import {
  etsyDeleteIntegration,
  etsyFetchListings,
  etsyFetchTransactions,
  etsyIntegrationSyncChange,
  etsySyncApi,
} from "src/apiService/modules/etsy";
import { bankAccountByIntegrationIdSelector } from "src/store/bankAccount/selector";
import { DataType } from "../../GetStarted/ImportDialog";
import { isEtsyAllowedToDirectImportSelector } from "src/store/uploads/selector";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import type MonthYear from "../../../interfaces/monthYear.interface";
import { setActiveDialog } from "src/store/adminHtml/actions";

// fix-vim-highlight = }

const IsRunningThreshold = 10 * 60 * 1000; // 10 min

function EtsyIntegrationTableRow({
  integration,
  setDialog,
  hideInventory,
}: {
  integration: EtsyIntegration;
  setDialog: Dispatch<SetStateAction<DialogState>>;
  hideInventory: boolean;
}) {
  const dispatch = useDispatch();
  const isRunning = useIsRunning(
    integration.salesIsRunning,
    integration.inventoryIsRunning,
    IsRunningThreshold
  );
  const bankAccount = useSelector((state) =>
    integration?.id
      ? bankAccountByIntegrationIdSelector(state, integration.id)
      : undefined
  );
  function usePreventImportEtsy(dataType: DataType) {
    return !useSelector((state: any) =>
      isEtsyAllowedToDirectImportSelector(state, dataType)
    );
  }

  return (
    <PlatformIntegrationsTableRow
      key={integration.id}
      name={bankAccount?.name || integration.username || "Etsy"}
      platform="Etsy"
      isRunning={isRunning}
      sync={integration.sync}
      inventory={integration.sync && integration.inventory !== false}
      showReconnectAction={!!integration.error}
      onOpenNotifications={() => dispatch(setActiveDialog("notifications"))}
      onPullDataNow={async () => {
        await etsySyncApi(integration.id);
        toast.success("Request received. Your imports are processing.");
      }}
      onImport={() => {
        setDialog({
          type: "import",
          props: {
            integrationId: integration.id,
            fetchListings: async () => {
              const resp = await etsyFetchListings();
              if (!resp.data) {
                return { success: true };
              }
            },
            fetchTransactions: async (
              integrationId: string,
              start: MonthYear,
              end: MonthYear
            ) => {
              const resp = await etsyFetchTransactions(start, end);
              if (!resp.data) {
                return { success: true };
              }
            },
            providerName: "Etsy",
            usePreventImport: usePreventImportEtsy,
          },
        });
      }}
      onSyncChanged={async (sync, inventory) => {
        await etsyIntegrationSyncChange(integration.id, sync, inventory);
        if ((inventory === undefined || inventory) && sync)
          await etsySyncApi(integration.id);
      }}
      hideInventory={hideInventory}
      onDelete={() =>
        setDialog({
          type: "delete",
          props: {
            integration,
            deleteIntegration: etsyDeleteIntegration,
          },
        })
      }
    />
  );
}

export default EtsyIntegrationTableRow;
